/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps, jsx-a11y/alt-text */
import React, { useState, useEffect } from "react";
import logo from "../../assets/images/pebble-logo.png";
import { useNavigate, useLocation, useParams, Link } from "react-router-dom";
import { doc, getDoc, getFirestore, collection, query, where, orderBy, limit, getDocs } from "firebase/firestore";
import { useTranslation } from "react-i18next";

import DashboardContent from "../../components/EO/DashboardContent";
import EventsContent from "../../components/EO/EventsContent";
import MarketplaceSellersContent from "../../components/EO/MarketplaceSellersContent";
import SettingsContent from "../../components/EO/SettingsContent";
import FloatingActionButton from "../../components/ui/FloatingActionButton";
import LegalModal from "../../components/ui/LegalModal";
import PaymentMethodReminder from "../../components/ui/PaymentMethodReminder";

import dashboard_home_icon from "../../assets/images/dashboard_icons/dashboard_home_icon.svg";
import dashboard_events_icon from "../../assets/images/dashboard_icons/dashboard_events_icon.svg";
import dashboard_settings_icon from "../../assets/images/dashboard_icons/dashboard_settings_icon.svg";
import dashboard_mps_icon from "../../assets/images/dashboard_icons/dashboard_marketplacesellers_icon.svg";

import dashboard_home_icon_active from "../../assets/images/dashboard_icons/dashboard_home_white_icon.svg";
import dashboard_events_icon_active from "../../assets/images/dashboard_icons/dashboard_events_white_icon.svg";
import dashboard_settings_icon_active from "../../assets/images/dashboard_icons/dashboard_settings_white_icon.svg";
import dashboard_mps_icon_active from "../../assets/images/dashboard_icons/dashboard_marketplacesellers_white_icon.svg";
import dashboard_receipts_icon from "../../assets/images/dashboard_icons/dashboard_receipts_icon.svg";
import dashboard_receipts_white_icon from "../../assets/images/dashboard_icons/dashboard_receipts_white_icon.svg";

import { useAuth } from "../../context/AuthContext";
import CreateEventContent from "../../components/EO/CreateEvent/CreateEventContent";
import Button from "../../components/button/button";
import { useBreadcrumb } from "../../context/BreadCrumbContext";
import {
  HelpCircle,
  BookOpen,
  ChevronDown,
  CreditCard,
  LogOut,
  Bell,
  Users,
  BarChart2,
  Calendar,
  LayoutDashboard,
  Store,
  Receipt,
  Ticket,
  Settings,
  ChevronRight,
  Grid3X3,
  FileText,
  Sparkles,
} from "lucide-react";
import WelcomeModal from "../../components/Onboarding/WelcomeModal";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import DarkModeToggle from "../../components/ui/DarkModeToggle";
import NotificationBell from "../../components/ui/NotificationBell";
import HelpBot from "../../components/EO/HelpBot";

const EODashboard = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [activeContent, setActiveContent] = useState("dashboard");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [legalModal, setLegalModal] = useState({ isOpen: false, type: null });
  const [showPaymentReminder, setShowPaymentReminder] = useState(false);
  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [showProWelcome, setShowProWelcome] = useState(false);
  const [hideUpgradeBanner, setHideUpgradeBanner] = useState(() => {
    return sessionStorage.getItem("hideUpgradeBanner") === "true";
  });
  const { signOut, user, subscriptionTier } = useAuth();
  const isPro = subscriptionTier === 'pro' || subscriptionTier === 'premium' || subscriptionTier === 'enterprise';
  const isAaron = user?.email === "aaron@pebbletheapp.com";
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const { id } = useParams();
  const { breadcrumb: crumbs } = useBreadcrumb();
  const db = getFirestore();

  // Load recent notification count for bell badge
  useEffect(() => {
    if (!user?.uid) return;
    const load = async () => {
      try {
        const snap = await getDocs(query(
          collection(db, "eoNotifications"),
          where("eoUserId", "==", user.uid),
          orderBy("sentAt", "desc"),
          limit(5)
        ));
        setNotifCount(snap.size);
      } catch { /* non-blocking */ }
    };
    load();
  }, [user?.uid]);

  useEffect(() => {
    const shouldShowWelcome = sessionStorage.getItem("showWelcomeBack");
    if (shouldShowWelcome === "true") {
      setShowWelcomeBack(true);
      sessionStorage.removeItem("showWelcomeBack");
      setTimeout(() => setShowWelcomeBack(false), 5000);
    }

    const shouldShowPro = sessionStorage.getItem("showProWelcome");
    if (shouldShowPro === "true") {
      setShowProWelcome(true);
      sessionStorage.removeItem("showProWelcome");
      setTimeout(() => setShowProWelcome(false), 8000);
    }
  }, []);

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!user?.uid) return;

      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const subscription = data.subscription || {};

          if (
            subscription.status === "expired" ||
            subscription.status === "canceled"
          ) {
            setIsSubscriptionExpired(true);
            setShowPaymentReminder(true);
          }
        }
      } catch (error) {
        console.error("Error checking subscription status:", error);
      }
    };

    checkSubscriptionStatus();
  }, [user?.uid, db]);

  const isCreateEventPage = location.pathname === "/eo/create-event";

  const showFloatingButton =
    ["/eo/dashboard", "/eo/events"].some((path) =>
      location.pathname.startsWith(path),
    ) && !isCreateEventPage;

  const navItems = [
    {
      id: "dashboard",
      lucideIcon: LayoutDashboard,
      textKey: "nav.dashboard",
      path: "/eo/dashboard",
    },
    {
      id: "events",
      lucideIcon: Calendar,
      textKey: "nav.events",
      paths: ["/eo/events", "/eo/create-event"],
    },
    {
      id: "marketplace",
      lucideIcon: Store,
      textKey: "nav.marketplaceVendors",
      paths: ["/eo/marketplace"],
    },
    {
      id: "booths",
      lucideIcon: Grid3X3,
      textKey: "nav.booths",
      path: "/eo/booths",
    },
    {
      id: "receipts",
      lucideIcon: Receipt,
      textKey: "nav.receipts",
      path: "/eo/receipts",
    },
    {
      id: "vendor-crm",
      lucideIcon: Users,
      textKey: "nav.vendorCRM",
      path: "/eo/vendor-crm",
      isPremium: true,
    },
    {
      id: "analytics",
      lucideIcon: BarChart2,
      textKey: "nav.analytics",
      path: "/eo/analytics",
      isPremium: true,
    },
    {
      id: "ticketing",
      lucideIcon: Ticket,
      textKey: "nav.ticketing",
      path: "/eo/ticketing",
      isPremium: true,
    },
    {
      id: "reports",
      lucideIcon: FileText,
      textKey: "nav.reports",
      path: "/eo/reports",
      isPremium: true,
    },
    {
      id: "growth",
      lucideIcon: Sparkles,
      textKey: "nav.growth",
      path: "/eo/growth",
      isPremium: false,
    },
    {
      id: "notifications",
      icon: null,
      activeIcon: null,
      textKey: "nav.notifications",
      path: "/eo/notifications",
      useLucide: true,
    },
    {
      id: "settings",
      lucideIcon: Settings,
      textKey: "nav.settings",
      paths: ["/eo/settings"],
    },
    {
      id: "tutorials",
      icon: null,
      activeIcon: null,
      textKey: "nav.tutorials",
      path: "/eo/tutorials",
      useLucide: true,
    },
    {
      id: "support",
      icon: null,
      activeIcon: null,
      textKey: "nav.support",
      path: "/eo/support",
      useLucide: true,
    },
  ];
  useEffect(() => {
    const currentPath = location.pathname;
    const currentNavItem = navItems.find((item) => {
      if (item.paths) {
        // Match any of the defined paths or their subpaths
        return item.paths.some((path) => currentPath.startsWith(path));
      }
      if (item.path) {
        // Match single path or its subpaths
        return currentPath.startsWith(item.path);
      }
      return false;
    });
    if (currentNavItem) {
      setActiveContent(currentNavItem.id);
    } else {
      setActiveContent("dashboard"); // Default to dashboard if no match found
    }
  }, [location.pathname, navItems]);

  const selectEvent = (event) => {
    setSelectedEvent(event); // Update selected event
    setActiveContent("createEvent"); // Set content to show CreateEventContent
  };

  const handlePaymentReminderClose = (paymentAdded) => {
    if (paymentAdded) {
      setShowPaymentReminder(false);
    } else if (!isSubscriptionExpired) {
      setShowPaymentReminder(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100 relative">
      <WelcomeModal />
        <PaymentMethodReminder
          isOpen={showPaymentReminder}
          onClose={handlePaymentReminderClose}
          isSubscriptionExpired={isSubscriptionExpired}
        />
        {/*sidebar backdrop overlay*/}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
        {/*sidebar*/}
        <aside
          className={`${
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          } fixed inset-y-0 left-0 z-40 md:sticky md:top-0 md:self-start md:h-screen md:translate-x-0 w-52 flex-shrink-0 overflow-y-auto transition-transform duration-200`}
          style={{
            borderRight: "1px solid var(--Grey-300, #D0D5DD)",
            background: "var(--White, #FEFEFE)",
            boxShadow:
              "0px 1px 3px 0px rgba(16, 24, 40, 0.10), 0px 1px 2px 0px rgba(16, 24, 40, 0.06)",
          }}
        >
          <div className="p-3 flex justify-center mt-4">
            <Link to="/">
              <img src={logo} alt="Pebble" className="h-10 w-auto" />
            </Link>
          </div>
          <nav className="mt-4 pb-4">
            <div className="mb-3">
              <h2 className="text-gray-400 text-xs font-medium uppercase tracking-wider px-4 mb-1">
                {t("sidebar.analyze")}
              </h2>
              {navItems
                .filter((item) => item.id === "dashboard")
                .map((item) => {
                  const Icon = item.lucideIcon;
                  const isActive = activeContent === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => { navigate(item.paths ? item.paths[0] : item.path); setIsSidebarOpen(false); }}
                      className={`flex items-center w-full h-10 px-4 py-2 text-sm text-left ${isActive ? "bg-primary-500 text-white" : "text-gray-700 hover:bg-gray-50"}`}
                    >
                      {Icon && <Icon className={`h-4 w-4 mr-2.5 flex-shrink-0 ${isActive ? "text-white" : "text-gray-700"}`} />}
                      {t(item.textKey)}
                    </button>
                  );
                })}
            </div>
            <div className="mb-3">
              <h2 className="text-gray-400 text-xs font-medium uppercase tracking-wider px-4 mb-1">
                {t("sidebar.manage")}
              </h2>
              {navItems
                .filter(
                  (item) =>
                    item.id === "events" ||
                    item.id === "marketplace" ||
                    item.id === "booths" ||
                    item.id === "receipts",
                )
                .map((item) => {
                  const LucideIcon = item.lucideIcon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        navigate(item.paths ? item.paths[0] : item.path);
                        setIsSidebarOpen(false);
                      }}
                      className={`flex items-center w-full h-10 px-4 py-2 text-sm text-left ${
                        activeContent === item.id
                          ? "bg-primary-500 text-white"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {LucideIcon ? (
                        <LucideIcon
                          className={`h-4 w-4 mr-2.5 flex-shrink-0 ${
                            activeContent === item.id ? "text-white" : "text-gray-700"
                          }`}
                        />
                      ) : (
                        <img
                          src={activeContent === item.id ? item.activeIcon : item.icon}
                          alt={t(item.textKey)}
                          className="h-4 w-4 mr-2.5 flex-shrink-0"
                        />
                      )}
                      {t(item.textKey)}
                    </button>
                  );
                })}
            </div>
            <div className="mb-3">
              <h2 className="text-gray-400 text-xs font-medium uppercase tracking-wider px-4 mb-1 flex items-center gap-1.5">
                {t("sidebar.pro")}
                {!isPro && (
                  <button
                    onClick={() => navigate("/eo-paywall")}
                    className="px-1.5 py-0.5 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-[9px] font-medium rounded-full hover:opacity-90 transition"
                  >
                    Upgrade
                  </button>
                )}
              </h2>
              {navItems
                .filter((item) => item.isPremium)
                .map((item) => {
                  const LucideIcon = item.lucideIcon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        navigate(item.paths ? item.paths[0] : item.path);
                        setIsSidebarOpen(false);
                      }}
                      className={`flex items-center w-full h-10 px-4 py-2 text-sm text-left ${
                        activeContent === item.id
                          ? "bg-primary-500 text-white"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {LucideIcon ? (
                        <LucideIcon
                          className={`h-4 w-4 mr-2.5 flex-shrink-0 ${
                            activeContent === item.id ? "text-white" : "text-gray-700"
                          }`}
                        />
                      ) : (
                        <img
                          src={activeContent === item.id ? item.activeIcon : item.icon}
                          alt={t(item.textKey)}
                          className="h-4 w-4 mr-2.5 flex-shrink-0"
                        />
                      )}
                      {t(item.textKey)}
                    </button>
                  );
                })}
            </div>
            {isAaron && (
              <div className="mb-3">
                <h2 className="text-gray-400 text-xs font-medium uppercase tracking-wider px-4 mb-1 flex items-center gap-1.5">
                  {t("sidebar.growth")}
                  <span className="px-1.5 py-0.5 bg-gray-900 text-white text-[9px] font-medium rounded-full">AI</span>
                </h2>
                {navItems
                  .filter((item) => item.id === "growth")
                  .map((item) => {
                    const Icon = item.lucideIcon;
                    const isActive = activeContent === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => { navigate(item.path); setIsSidebarOpen(false); }}
                        className={`flex items-center w-full h-10 px-4 py-2 text-sm text-left ${isActive ? "bg-primary-500 text-white" : "text-gray-700 hover:bg-gray-50"}`}
                      >
                        {Icon && <Icon className={`h-4 w-4 mr-2.5 flex-shrink-0 ${isActive ? "text-white" : "text-gray-700"}`} />}
                        {t(item.textKey)}
                      </button>
                    );
                  })}
              </div>
            )}
            <div className="mb-3">
              <h2 className="text-gray-400 text-xs font-medium uppercase tracking-wider px-4 mb-1">
                {t("sidebar.preference")}
              </h2>
              {navItems
                .filter((item) => item.id === "settings")
                .map((item) => {
                  const Icon = item.lucideIcon;
                  const isActive = activeContent === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => { navigate(item.paths ? item.paths[0] : item.path); setIsSidebarOpen(false); }}
                      className={`flex items-center w-full h-10 px-4 py-2 text-sm text-left ${isActive ? "bg-primary-500 text-white" : "text-gray-700 hover:bg-gray-50"}`}
                    >
                      {Icon && <Icon className={`h-4 w-4 mr-2.5 flex-shrink-0 ${isActive ? "text-white" : "text-gray-700"}`} />}
                      {t(item.textKey)}
                    </button>
                  );
                })}
              <button
                onClick={() => {
                  navigate("/eo/notifications");
                  setIsSidebarOpen(false);
                }}
                className={`flex items-center w-full h-10 px-4 py-2 text-sm text-left ${
                  activeContent === "notifications"
                    ? "bg-primary-500 text-white"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Bell
                  className={`h-4 w-4 mr-2.5 flex-shrink-0 ${
                    activeContent === "notifications"
                      ? "text-white"
                      : "text-gray-700"
                  }`}
                />
                {t("nav.notifications")}
              </button>
              <button
                onClick={() => {
                  navigate("/eo/tutorials");
                  setIsSidebarOpen(false);
                }}
                className={`flex items-center w-full h-10 px-4 py-2 text-sm text-left ${
                  activeContent === "tutorials"
                    ? "bg-primary-500 text-white"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <BookOpen
                  className={`h-4 w-4 mr-2.5 flex-shrink-0 ${
                    activeContent === "tutorials"
                      ? "text-white"
                      : "text-gray-700"
                  }`}
                />
                {t("nav.tutorials")}
              </button>
              <button
                onClick={() => {
                  navigate("/eo/support");
                  setIsSidebarOpen(false);
                }}
                className={`flex items-center w-full h-10 px-4 py-2 text-sm text-left ${
                  activeContent === "support"
                    ? "bg-primary-500 text-white"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <HelpCircle
                  className={`h-4 w-4 mr-2.5 flex-shrink-0 ${
                    activeContent === "support" ? "text-white" : "text-gray-700"
                  }`}
                />
                {t("nav.support")}
              </button>
            </div>
          </nav>
        </aside>
        <div className="flex-1 flex flex-col min-w-0">
          {/*top nav bar*/}
          <header className="bg-white shadow-sm border border-gray-300 top-0">
            <div className="px-4 h-[80px] justify-between content-center sm:px-6 lg:px-8">
              <div className="flex items-center justify-between ">
                <button
                  onClick={toggleSidebar}
                  className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                >
                  <span className="sr-only">Open sidebar</span>
                  <svg
                    className="h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </button>

                {/* Mobile: show logo */}
                <img
                  src={logo}
                  alt="Pebble"
                  className="h-8 w-auto ml-2 md:hidden"
                />
                {/* Desktop: show breadcrumb title */}
                <h2 className="ml-3 text-xl font-medium text-gray-900 leading-6 truncate hidden md:block">
                  {activeContent === "dashboard" ? (
                    t("nav.dashboard")
                  ) : (
                    <>
                      {t("nav.dashboard")}
                      <ChevronRight className="mx-1 -mt-0.5 h-4 w-4 inline text-gray-400" />
                      <strong className="font-semibold">
                        {t(
                          navItems.find((item) => item.id === activeContent)
                            ?.textKey,
                        )}
                      </strong>
                      {id && (
                        <>
                          <ChevronRight className="mx-1 -mt-0.5 h-4 w-4 inline text-gray-400" />
                          <strong className="font-semibold">
                            {crumbs?.eventName}
                          </strong>
                        </>
                      )}
                    </>
                  )}
                </h2>

                <div className="flex items-center">
                  <div className="ml-3 flex flex-row items-center gap-4 mr-2 sm:mr-10 pr-2 sm:pr-6">
                    <DarkModeToggle variant="icon" />
                    <LanguageSwitcher />
                    {/* Notification bell */}
                    <NotificationBell userId={user?.uid} recipientType="eo" />
                    <div className="relative">
                      <button
                        onClick={() => setShowAccountMenu(!showAccountMenu)}
                        className="flex items-center gap-1.5 text-sm font-medium text-grey-600 hover:text-grey-900 transition-colors"
                      >
                        Account
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${
                            showAccountMenu ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      {showAccountMenu && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setShowAccountMenu(false)}
                          />
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-grey-200 py-1 z-20">
                            <button
                              onClick={() => {
                                navigate("/plan-selection");
                                setShowAccountMenu(false);
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-grey-700 hover:bg-grey-50 transition-colors"
                            >
                              <CreditCard className="w-4 h-4" />
                              Change Plan
                            </button>
                            <button
                              onClick={() => {
                                signOut();
                                setShowAccountMenu(false);
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <LogOut className="w-4 h-4" />
                              {t("navigation.logout")}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Welcome to Pro Banner */}
          {showProWelcome && (
            <div className="mx-6 mt-4 bg-gradient-to-r from-purple-50 to-indigo-100 border border-purple-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-lg">&#9734;</span>
                </div>
                <div>
                  <h3 className="font-semibold text-grey-900">Welcome to Pro!</h3>
                  <p className="text-sm text-grey-600">
                    Your Pro plan is now active. You now have access to Vendor CRM, Analytics, Ticketing, and Reports.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowProWelcome(false)}
                className="text-grey-400 hover:text-grey-600 p-1"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Welcome Back Banner */}
          {showWelcomeBack && (
            <div className="mx-6 mt-4 bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-lg">👋</span>
                </div>
                <div>
                  <h3 className="font-semibold text-grey-900">Welcome back!</h3>
                  <p className="text-sm text-grey-600">
                    Great to see you again. Your account is all set up and ready
                    to go.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowWelcomeBack(false)}
                className="text-grey-400 hover:text-grey-600 p-1"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Upgrade to Pro Banner - shown for free/basic users */}
          {!isPro && !hideUpgradeBanner && (
            <div className="mx-6 mt-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-lg">⚡</span>
                </div>
                <div>
                  <h3 className="font-semibold text-grey-900">Upgrade to Pro</h3>
                  <p className="text-sm text-grey-600">
                    Unlock Vendor CRM, Analytics, Ticketing, Reports, and more advanced features.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate("/eo-paywall")}
                  className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all shadow-sm"
                >
                  Upgrade Now
                </button>
                <button
                  onClick={() => {
                    setHideUpgradeBanner(true);
                    sessionStorage.setItem("hideUpgradeBanner", "true");
                  }}
                  className="text-grey-400 hover:text-grey-600 p-1"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Page content */}
          <main className="flex-1 mx-2 my-2 sm:mx-4 sm:my-4 lg:mx-6 lg:my-6 bg-white rounded-lg shadow-md">
            <div key={location.pathname} className="page-slide-in">
              {children}
            </div>
          </main>

          {/* Floating Action Button - Create Event (only on Dashboard, Events, Marketplace) */}
          {showFloatingButton && (
            <FloatingActionButton
              onClick={() => navigate("/eo/create-event")}
            />
          )}

          {/* Footer */}
          <footer className="w-full">
            <div className="max-w-7xl mx-auto py-3 px-4 sm:px-6 lg:px-8">
              <p className="text-center text-xs text-gray-400">
                {t("footer.copyright")} ·{" "}
                <button
                  onClick={() => setLegalModal({ isOpen: true, type: "terms" })}
                  className="hover:text-gray-600"
                >
                  {t("footer.terms")}
                </button>{" "}
                ·{" "}
                <button
                  onClick={() =>
                    setLegalModal({ isOpen: true, type: "privacy" })
                  }
                  className="hover:text-gray-600"
                >
                  {t("footer.privacyPolicy")}
                </button>
              </p>
            </div>
          </footer>

          <LegalModal
            isOpen={legalModal.isOpen}
            onClose={() => setLegalModal({ isOpen: false, type: null })}
            type={legalModal.type}
          />
        </div>
    <HelpBot />
    </div>
  );
};
export default EODashboard;
